import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * Phase 44 — Auth callback handler.
 *
 * Renders ONLY when the URL hash contains `session_id=<token>`.
 * Exchanges the session_id with the backend, sets the httpOnly cookie,
 * refreshes the user, then navigates to the right post-login page.
 *
 * Uses useRef (not useState) for the processed flag so StrictMode double
 * invocation doesn't fire the exchange twice.
 */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.hash.slice(1));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }

    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });
      } catch (err) {
        // Soft-deleted user, network error, or bad session_id
        const detail =
          err?.response?.data?.detail || "Authentication failed";
        navigate("/?auth_error=" + encodeURIComponent(detail), { replace: true });
        return;
      }
      const u = await refresh();
      // Strip the session_id fragment from the URL
      window.history.replaceState(null, "", window.location.pathname);
      if (!u) {
        navigate("/", { replace: true });
        return;
      }
      if (!u.profile_complete) {
        navigate("/profile-setup", { replace: true });
      } else if (u.status === "PENDING") {
        navigate("/pending", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
      <div className="text-center space-y-2" data-testid="auth-callback-spinner">
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
          Verifying Google sign-in...
        </div>
        <div className="text-2xl font-heading">StoxTrade</div>
      </div>
    </div>
  );
};
